import {
  queryOptions,
  infiniteQueryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  StartQvdAnalysisInput,
  QvdAnalysisRunStatus,
  QvdFilesSort,
  QvdRunDiffSide,
  QvdRunLogLevel,
} from "@repo/shared";

import {
  startQvdAnalysis,
  getQvdScopeOptions,
  getQvdAnalysisRun,
  listQvdAnalysisRuns,
  listQvdOrphans,
  listQvdUnloadedColumns,
  getQvdConsumingApps,
  getQvdFileConsumingApps,
  listQvdFiles,
  listQvdDirectory,
  compareQvdRuns,
  listQvdDiffFiles,
  listQvdRunLogs,
  cancelQvdAnalysisRun,
  reprocessQvdAnalysis,
  resumeQvdAnalysis,
  deleteQvdAnalysisRun,
} from "@/lib/api/qvd-analysis";

const ACTIVE_STATUSES: QvdAnalysisRunStatus[] = ["pending", "processing", "cancelling"];

export function isRunActive(status: QvdAnalysisRunStatus | undefined): boolean {
  return !!status && ACTIVE_STATUSES.includes(status);
}

export const qvdAnalysisKeys = {
  all: ["qvd-analysis"] as const,
  runsLists: () => [...qvdAnalysisKeys.all, "runs-list"] as const,
  runsList: (filters?: { connectionId?: string }) =>
    [...qvdAnalysisKeys.runsLists(), filters] as const,
  // The list screen pages the same endpoint by cursor; under runsLists() so one
  // invalidation (start, cancel, delete) refreshes both shapes.
  runsInfinite: (filters?: { connectionId?: string }) =>
    [...qvdAnalysisKeys.runsLists(), "infinite", filters] as const,
  scopeOptions: (connectionId: string) =>
    [...qvdAnalysisKeys.all, "scope-options", connectionId] as const,
  run: (id: string) => [...qvdAnalysisKeys.all, "run", id] as const,
  orphans: (runId: string) => [...qvdAnalysisKeys.all, "orphans", runId] as const,
  // columns(runId) stays the PREFIX both column lists share, so one
  // invalidateQueries still refreshes them together; columnsList adds the
  // population ("complete" = QVDs with a verdict, "incomplete" = verdict
  // withheld), which the two tabs must not share a cache entry for.
  columns: (runId: string) => [...qvdAnalysisKeys.all, "columns", runId] as const,
  columnsList: (runId: string, evidence: "complete" | "incomplete") =>
    [...qvdAnalysisKeys.columns(runId), evidence] as const,
  consumingApps: (runId: string, rowId: string) =>
    [...qvdAnalysisKeys.all, "consuming-apps", runId, rowId] as const,
  // Its own namespace: a column row and a file row are different tables, and
  // one's id could collide with the other's.
  fileConsumingApps: (runId: string, fileId: string) =>
    [...qvdAnalysisKeys.all, "file-consuming-apps", runId, fileId] as const,
  files: (runId: string, filters?: { search?: string; space?: string; sort?: QvdFilesSort }) =>
    [...qvdAnalysisKeys.all, "files", runId, filters ?? {}] as const,
  // One level of the space drill-down; the prefix is part of the key so each
  // level of the tree gets its own cache entry.
  directories: (runId: string, space: string, prefix: string) =>
    [...qvdAnalysisKeys.all, "directories", runId, space, prefix] as const,
  logs: (runId: string, filters?: { level?: QvdRunLogLevel }) =>
    [...qvdAnalysisKeys.all, "logs", runId, filters ?? {}] as const,
  compare: (runId: string, baseId: string) =>
    [...qvdAnalysisKeys.all, "compare", runId, baseId] as const,
  diffFiles: (
    runId: string,
    baseId: string,
    side: QvdRunDiffSide,
    filters?: { search?: string; space?: string; sort?: QvdFilesSort },
  ) => [...qvdAnalysisKeys.compare(runId, baseId), "files", side, filters ?? {}] as const,
};

// The runs list drives the section home; it polls while any run is active.
export const qvdAnalysisRunsListQueryOptions = (params?: {
  connectionId?: string;
  limit?: number;
}) =>
  queryOptions({
    queryKey: qvdAnalysisKeys.runsList({ connectionId: params?.connectionId }),
    queryFn: () =>
      listQvdAnalysisRuns({ limit: params?.limit ?? 100, connectionId: params?.connectionId }),
    refetchInterval: (query) =>
      query.state.data?.data.some((r) => isRunActive(r.status)) ? 5000 : false,
  });

// The list screen's flavor of the runs list: same first request (newest 100),
// plus the cursor pages behind it, pulled on demand ("load older analyses") so
// the screen's search and filters can cover them.
export const qvdAnalysisRunsInfiniteQueryOptions = (params?: {
  connectionId?: string;
  limit?: number;
}) =>
  infiniteQueryOptions({
    queryKey: qvdAnalysisKeys.runsInfinite({ connectionId: params?.connectionId }),
    queryFn: ({ pageParam }) =>
      listQvdAnalysisRuns({
        limit: params?.limit ?? 100,
        connectionId: params?.connectionId,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) => page.data.some((r) => isRunActive(r.status)))
        ? 5000
        : false,
  });

export const qvdAnalysisRunQueryOptions = (id: string) =>
  queryOptions({
    queryKey: qvdAnalysisKeys.run(id),
    queryFn: () => getQvdAnalysisRun(id),
    enabled: !!id,
    refetchInterval: (query) => (isRunActive(query.state.data?.status) ? 2000 : false),
  });

// Fetched live from Qlik on demand (when the run drawer's connection is chosen).
export const qvdScopeOptionsQueryOptions = (connectionId: string) =>
  queryOptions({
    queryKey: qvdAnalysisKeys.scopeOptions(connectionId),
    queryFn: () => getQvdScopeOptions(connectionId),
    enabled: false,
    staleTime: 60_000,
  });

export const qvdOrphansInfiniteQueryOptions = (runId: string) =>
  infiniteQueryOptions({
    queryKey: qvdAnalysisKeys.orphans(runId),
    queryFn: ({ pageParam }) =>
      listQvdOrphans(runId, { cursor: pageParam as string | undefined, limit: 200 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!runId,
  });

export const qvdUnloadedColumnsInfiniteQueryOptions = (
  runId: string,
  evidence: "complete" | "incomplete" = "complete",
) =>
  infiniteQueryOptions({
    queryKey: qvdAnalysisKeys.columnsList(runId, evidence),
    queryFn: ({ pageParam }) =>
      listQvdUnloadedColumns(runId, {
        cursor: pageParam as string | undefined,
        limit: 200,
        evidence,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!runId,
  });

// Per-QVD consumer detail, fetched only while its drawer is open (`enabled`) —
// the matrix is deliberately absent from the list payload. A finished run is
// immutable, so the answer never goes stale.
export const qvdConsumingAppsQueryOptions = (runId: string, rowId: string, enabled: boolean) =>
  queryOptions({
    queryKey: qvdAnalysisKeys.consumingApps(runId, rowId),
    queryFn: () => getQvdConsumingApps(runId, rowId),
    enabled: enabled && !!runId && !!rowId,
    staleTime: Infinity,
  });

// Same detail, keyed by a row of the file inventory instead of a column row.
export const qvdFileConsumingAppsQueryOptions = (runId: string, fileId: string, enabled: boolean) =>
  queryOptions({
    queryKey: qvdAnalysisKeys.fileConsumingApps(runId, fileId),
    queryFn: () => getQvdFileConsumingApps(runId, fileId),
    enabled: enabled && !!runId && !!fileId,
    staleTime: Infinity,
  });

// Server-side search/space-filter/sort — the query key carries the params so a
// filter change refetches from page 1; pages are pulled incrementally on scroll
// (NOT auto-loaded), since a run can hold hundreds of thousands of files.
export const qvdFilesInfiniteQueryOptions = (
  runId: string,
  filters: { search?: string; space?: string; sort: QvdFilesSort },
) =>
  infiniteQueryOptions({
    queryKey: qvdAnalysisKeys.files(runId, filters),
    queryFn: ({ pageParam }) =>
      listQvdFiles(runId, {
        cursor: pageParam as string | undefined,
        limit: 200,
        sort: filters.sort,
        search: filters.search || undefined,
        space: filters.space || undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!runId,
  });

// One level of a space's directory tree, aggregated server-side. A finished run
// is immutable, so each level is fetched once and then cached as the user walks
// back up the breadcrumb — but a still-running run keeps growing its inventory,
// so while `active` its levels must not be cached.
export const qvdDirectoriesQueryOptions = (
  runId: string,
  space: string,
  prefix: string,
  active: boolean,
) =>
  queryOptions({
    queryKey: qvdAnalysisKeys.directories(runId, space, prefix),
    queryFn: () => listQvdDirectory(runId, { space, prefix }),
    enabled: !!runId && !!space,
    staleTime: active ? 0 : Infinity,
  });

// Diff between two completed runs of the same connection + scope. Both runs are
// terminal, so the answer is immutable — no polling, no refetch.
export const qvdRunCompareQueryOptions = (runId: string, baseId: string) =>
  queryOptions({
    queryKey: qvdAnalysisKeys.compare(runId, baseId),
    queryFn: () => compareQvdRuns(runId, baseId),
    enabled: !!runId && !!baseId,
    staleTime: Infinity,
  });

// One side of that diff, with the same server-side search/space/sort and
// incremental paging as the inventory list.
export const qvdDiffFilesInfiniteQueryOptions = (
  runId: string,
  baseId: string,
  side: QvdRunDiffSide,
  filters: { search?: string; space?: string; sort: QvdFilesSort },
) =>
  infiniteQueryOptions({
    queryKey: qvdAnalysisKeys.diffFiles(runId, baseId, side, filters),
    queryFn: ({ pageParam }) =>
      listQvdDiffFiles(runId, baseId, side, {
        cursor: pageParam as string | undefined,
        limit: 200,
        sort: filters.sort,
        search: filters.search || undefined,
        space: filters.space || undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!runId && !!baseId,
  });

// Chronological worker execution-log stream. The level filter runs server-side
// (the query key carries it, so a change refetches from page 1); while the run
// is active the loaded pages poll so new lines stream in live.
export const qvdRunLogsInfiniteQueryOptions = (
  runId: string,
  filters: { level?: QvdRunLogLevel },
  active: boolean,
) =>
  infiniteQueryOptions({
    queryKey: qvdAnalysisKeys.logs(runId, filters),
    queryFn: ({ pageParam }) =>
      listQvdRunLogs(runId, {
        cursor: pageParam as string | undefined,
        limit: 200,
        level: filters.level,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!runId,
    refetchInterval: active ? 5000 : false,
  });

export function useStartQvdAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: StartQvdAnalysisInput) => startQvdAnalysis(input),
    onSuccess: ({ run }) => {
      queryClient.setQueryData(qvdAnalysisKeys.run(run.id), run);
      queryClient.invalidateQueries({ queryKey: qvdAnalysisKeys.runsLists() });
    },
  });
}

export function useCancelQvdAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelQvdAnalysisRun(id),
    onSuccess: (run) => {
      queryClient.setQueryData(qvdAnalysisKeys.run(run.id), run);
      queryClient.invalidateQueries({ queryKey: qvdAnalysisKeys.runsLists() });
    },
  });
}

// Re-opens the current run to re-analyze its retriable not-analyzable QVDs; the
// merged coverage lands back on the same run, so we stay on it and just refresh.
export function useReprocessQvdAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reprocessQvdAnalysis(id),
    onSuccess: (run) => {
      queryClient.setQueryData(qvdAnalysisKeys.run(run.id), run);
      queryClient.invalidateQueries({ queryKey: qvdAnalysisKeys.runsLists() });
    },
  });
}

// Re-opens the current run to CONTINUE the QVDs it never reached
// ("not_processed"); the merged coverage lands back on the same run, so we stay
// on it and just refresh.
export function useResumeQvdAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resumeQvdAnalysis(id),
    onSuccess: (run) => {
      queryClient.setQueryData(qvdAnalysisKeys.run(run.id), run);
      queryClient.invalidateQueries({ queryKey: qvdAnalysisKeys.runsLists() });
    },
  });
}

export function useDeleteQvdAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteQvdAnalysisRun(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qvdAnalysisKeys.runsLists() });
    },
  });
}
